// Pure logic behind the document view: how the selected orders become documents,
// what is missing, and the numbers the paper prints. Mirrors the backend
// (generate_pdf, invoice(), shipping_note()) so the paper shows what will be generated.

export const isEmpty = (v) => v === undefined || v === null || String(v).trim() === '';

export const num = (v) => {
    const n = parseFloat(String(v ?? '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
};

// Python's f'{x:,.2f}'
export const fmt2 = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Python's str(float): 20000.0, 0.5, 486.06000000000006 (both print the shortest repr that round-trips)
export const pyFloat = (x) => (Number.isInteger(x) ? x.toFixed(1) : String(x));

// the value a field will have: what the user typed, else what generating fills in
export const effective = (order, field, fills) => (isEmpty(order[field]) ? (fills?.[field] ?? '') : order[field]);

// Distinct values of a field over the positions of one document
export const distinct = (orders, indices, field) => [...new Set(indices.map((i) => String(orders[i][field] ?? '')))];

/**
 * One document per value of the grouping key, like generate_pdf. An empty key gets a new number on
 * generate, so each such order is its own document, unless the button combines them (combine_invoice).
 * Sorted by key like the backend; new ones last, in selection order.
 */
export function groupDocuments(orders, groupBy, combineKey) {
    const byKey = new Map();
    const fresh = [];
    orders.forEach((order, i) => {
        let key = String(order[groupBy] ?? '').trim();
        if (!key && combineKey === groupBy) key = 'combined';
        if (!key) {
            fresh.push({ key: '', indices: [i] });
            return;
        }
        if (!byKey.has(key)) byKey.set(key, []);
        byKey.get(key).push(i);
    });
    const keyed = [...byKey.entries()]
        .sort(([a], [b]) => (a === 'combined') - (b === 'combined') || a.localeCompare(b))
        .map(([key, indices]) => ({ key, indices }));
    return [...keyed, ...fresh];
}

// Positions split into files of `perFile` (the backend starts a new PDF after every 4 positions)
export const fileBreaks = (count, perFile) => Math.max(1, Math.ceil(count / perFile));

/**
 * What stands in the way of one document: required fields that are empty and won't be filled (blocking),
 * header fields with different values per position (warning: the last position wins), article problems.
 */
export function issuesOf(doc, orders, spec, context, label) {
    const out = [];
    spec.required.forEach((field) => {
        const missing = doc.indices.filter((i) => isEmpty(effective(orders[i], field, context[orders[i].id]?.fills)));
        if (missing.length) {
            const where = spec.header.includes(field) || spec.line.includes(field) ? '' : ' (under Other order fields)';
            const positions = doc.indices.length > 1 ? ` on ${missing.length} of ${doc.indices.length} positions` : '';
            out.push({ level: 'error', text: `${label(field)} is empty${positions}${where}` });
        }
    });
    spec.header.forEach((field) => {
        if (field === spec.group_by) return;
        const values = distinct(orders, doc.indices, field);
        if (values.length > 1) {
            out.push({ level: 'warning', field, text: `${values.length} different values for ${label(field)}: the last position's is printed` });
        }
    });
    doc.indices.forEach((i) => {
        const note = context[orders[i].id]?.article_note;
        if (note && !context[orders[i].id]?.article) {
            out.push({ level: 'error', text: `Position ${orders[i].position_ID}: ${note}` });
        } else if (note) {
            out.push({ level: 'warning', text: `Position ${orders[i].position_ID}: ${note}` });
        }
    });
    if (spec.group_by === 'shipping_ID') {
        doc.indices.forEach((i) => {
            const o = orders[i];
            const b = String(o.batches ?? '').split(';').length;
            const q = String(o.batch_quantities ?? '').split(';').length;
            if (String(o.batches ?? '').includes(';') && b !== q) {
                out.push({ level: 'warning', text: `Position ${o.position_ID}: ${b} batches but ${q} batch quantities` });
            }
        });
    }
    return out;
}

// ---- invoice numbers (invoice(), generate_pdf, get_table_values) ----

export function invoiceLine(order, ctx) {
    const a = ctx?.article;
    if (!a) return null;
    const unit = parseInt(a.display_unit, 10) || 1;
    const qty = num(order.quantity);
    const total = num(a.unit_price) * qty; // invoice() recalculates total_net from the article
    const taxable = String(order.taxable ?? '').toLowerCase() !== 'no';
    const german = ctx.customer?.nationality === 'DE';
    const tax = taxable && german ? +(total * 0.19).toFixed(2) : 0;
    return {
        displayQuant: `${pyFloat(qty / unit)} x ${a.display_unit}`,
        displayUnitPrice: fmt2(unit * num(a.unit_price)),
        total: +total.toFixed(2),
        tax,
        vatLabel: taxable && german ? '19%' : 'Not taxable in Germany',
    };
}

// ---- shipping note numbers (shipping_note(), calculate_dn_data) ----

export function shippingLine(order, ctx) {
    const f = ctx?.fills || {};
    const palettes = order.palettes;
    const hasPalettes = !['', '0', 0, undefined, null].includes(palettes);
    const gross = num(effective(order, 'gross_weight', f));
    const paletteWeight = hasPalettes ? num(palettes) * num(effective(order, 'weight_palette', f)) : 0;
    // WEIGHT_GROSS_INCL_PALETTES is rounded to 2 decimals per position, then summed unrounded
    return { hasPalettes, paletteWeight, totalWeight: parseFloat((gross + paletteWeight).toFixed(2)) };
}
